import { Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Typography, Box } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import MultipleAndSingleImgValidateWithPreview from 'src/common/FileValidation/MultipleAndSingleImgValidateWithPreview';
import { TOKEN_KEY, url, imgUrl } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { IImportantMessage, FormValues } from './types';

type UpdateImportantMessageProps = {
  messageItem: IImportantMessage;
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
};

function UpdateImportantMessage({ messageItem, handleClose, setRefreshTrigger }: UpdateImportantMessageProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      name: messageItem?.name || '',
      designation: messageItem?.designation || '',
      message: messageItem?.message || '',
      order: messageItem?.order ?? messageItem?.rank ?? '',
      status: messageItem?.status ?? 1,
    }
  });

  const [loading, setLoading] = useState<boolean>(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    if (messageItem) {
      reset({
        name: messageItem.name || '',
        designation: messageItem.designation || '',
        message: messageItem.message || '',
        order: messageItem.order ?? messageItem.rank ?? '',
        status: messageItem.status ?? 1,
      });
    }
  }, [messageItem, reset]);

  const onSubmit = async (values: FormValues) => {
    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token is missing. Please log in again.',
        });
        return;
      }

      setLoading(true);

      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('designation', values.designation);
      formData.append('message', values.message);
      formData.append('rank', String(values.order));
      formData.append('order', String(values.order));
      formData.append('status', String(values.status));

      if (values.image && values.image[0]) {
        formData.append('image', values.image[0]);
      }

      const { data } = await axios.patch(
        `${url}/api/v1/admin/important-message/${messageItem.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: data.message || 'Important message updated successfully!',
        });
        setRefreshTrigger((prev) => prev + 1);
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: data.message || 'Failed to update important message.',
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err?.response?.data?.message || 'Something went wrong'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1, p: 1 }}>
      <Grid container spacing={2}>
        {/* Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='name'
            label='Name'
            type='text'
            variant='outlined'
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', { required: 'Name is required' })}
          />
        </Grid>

        {/* Designation */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='designation'
            label='Designation'
            type='text'
            variant='outlined'
            error={!!errors.designation}
            helperText={errors.designation?.message}
            {...register('designation', { required: 'Designation is required' })}
          />
        </Grid>

        {/* Message */}
        <Grid item xs={12}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='message'
            label='Message'
            multiline
            rows={4}
            variant='outlined'
            error={!!errors.message}
            helperText={errors.message?.message}
            {...register('message', { required: 'Message is required' })}
          />
        </Grid>

        {/* Order */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='order'
            label='Display Order'
            type='number'
            inputProps={{ min: 1, step: 1 }}
            variant='outlined'
            error={!!errors.order}
            helperText={errors.order?.message}
            {...register('order', { required: 'Display order is required' })}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant='outlined' margin='normal'>
            <InputLabel id='status-label'>Status</InputLabel>
            <Select
              labelId='status-label'
              id='status'
              label='Status'
              defaultValue={messageItem?.status ?? 1}
              {...register('status')}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Current Image */}
        {messageItem?.image && (
          <Grid item xs={12}>
            <Box mb={1}>
              <Typography variant='body2' color='textSecondary' mb={1}>
                Current Image:
              </Typography>
              <img
                src={`${imgUrl}/${messageItem.image}`}
                alt='Current Message user'
                style={{
                  maxWidth: '120px',
                  maxHeight: '120px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                }}
              />
            </Box>
          </Grid>
        )}

        {/* Image Upload */}
        <Grid item xs={12}>
          <MultipleAndSingleImgValidateWithPreview
            register={register}
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            label='Upload New Image (Optional)'
            fieldName='image'
            hideCustomLabel
            onlyImage
            mode='single'
          />
        </Grid>

        {/* Action Button */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {loading ? 'Updating...' : 'Update Important Message'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UpdateImportantMessage;
